import React, { useEffect, useRef } from 'react';
import IconToggleArrow from '@pano-project/assets/icons/IconToggleArrow';
import { getCountryFlagImage } from 'utils/helper-functions/getCountryFlagImage';
import { LazyImage } from '@pano-project/components/LazyImage/LazyImage';
import CountrySelector from '../CountrySelector/CountrySelector';
import './PhoneInput.sass';

const PhoneInputComposite = ({
  name,
  codes,
  prefix,
  number,
  countryCode,
  isModalOpen,
  setIsModalOpen,
  onNumberChange,
  onPrefixChange,
  onFocus,
  onBlur,
  placeholder,
  disabled,
  required,
  langBack,
  s,
  autoFocus = false,
}) => {
  const numberInputRef = useRef(null);
  const triggerRef = useRef(null);
  const shellRef = useRef(null);
  const isInternalMouseDownRef = useRef(false);
  const isModalOpenRef = useRef(isModalOpen);
  const listboxId = `${name || 'phone'}-dial-code-listbox`;

  isModalOpenRef.current = isModalOpen;

  useEffect(() => {
    if (autoFocus && numberInputRef.current && !disabled) {
      const id = requestAnimationFrame(() => {
        // Opening the picker focuses the field first; keep the keyboard in the picker's search box.
        if (isModalOpenRef.current) return;
        numberInputRef.current?.focus();
      });
      return () => cancelAnimationFrame(id);
    }
  }, [autoFocus, disabled]);

  const handleShellMouseDown = () => {
    isInternalMouseDownRef.current = true;
    setTimeout(() => {
      isInternalMouseDownRef.current = false;
    }, 0);
  };

  const handleInternalBlur = (e) => {
    if (e.relatedTarget && shellRef.current?.contains(e.relatedTarget)) return;

    if (isInternalMouseDownRef.current) {
      isInternalMouseDownRef.current = false;
      return;
    }

    onBlur && onBlur();
  };

  const handleModalClose = (reason) => {
    setIsModalOpen(false);
    if (reason === 'outside') return;
    requestAnimationFrame(() => numberInputRef.current?.focus());
  };

  return (
    <div ref={shellRef} className="phone-input-shell" onMouseDown={handleShellMouseDown}>
      <div className={`phone-input-composite ${disabled ? 'disabled' : ''}`}>
        <button
          ref={triggerRef}
          type="button"
          name="phone_prefix"
          className={`phone-input__trigger ${s ? s('input') : ''}`}
          onClick={() => !disabled && setIsModalOpen(!isModalOpen)}
          onFocus={() => onFocus && onFocus(name)}
          onBlur={handleInternalBlur}
          disabled={disabled}
          aria-label="Country dial code"
          aria-haspopup="listbox"
          aria-expanded={isModalOpen}
        >
          {getCountryFlagImage(countryCode) && (
            <span className="phone-input__trigger-flag">
              <LazyImage src={getCountryFlagImage(countryCode)} alt={countryCode} />
            </span>
          )}
          <span className="phone-input__trigger-value">+{prefix}</span>
          <span className={`phone-input__trigger-arrow ${isModalOpen ? 'opened' : ''}`}>
            <IconToggleArrow />
          </span>
        </button>
        <span className="phone-input__splitter" aria-hidden="true" />
        <input
          ref={numberInputRef}
          name={name}
          type="tel"
          value={number}
          onChange={onNumberChange}
          onFocus={() => onFocus && onFocus(name)}
          onBlur={handleInternalBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={`${s ? s('input') : ''} phone-input__number`}
          inputMode="numeric"
          autoComplete="tel-national"
        />
      </div>
      <CountrySelector
        mode="regionCode"
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onDialCodeChange={onPrefixChange}
        selectedCountryCode={countryCode}
        selectedValue={prefix}
        dialCodes={codes}
        langBack={langBack || ((k) => k)}
        hideFlagsInRegionCode={false}
        outsideClickIgnoreRefs={[triggerRef]}
        listboxId={listboxId}
      />
    </div>
  );
};

export default PhoneInputComposite;
